import { useMemo } from 'react';
import { useQueries, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { isBefore } from 'date-fns';
import { getRequest } from '../../../../core/apiRequests';
import { getSalesOpp } from '../../../../api/report';
import { ACTIVITIES_TYPES_ASSO_REPORT } from '../../../../constants';

const CACHE_TIME = 1000 * 60 * 60 * 2; // 2 hours
const STALE_TIME = 1000 * 60 * 60 * 1.5; // 1.5 hours

const allAssociatePerformanceTypes = [
  ACTIVITIES_TYPES_ASSO_REPORT.aiInsights,
  ACTIVITIES_TYPES_ASSO_REPORT.activities,
  ACTIVITIES_TYPES_ASSO_REPORT.chatMessages,
  ACTIVITIES_TYPES_ASSO_REPORT.newClients,
  ACTIVITIES_TYPES_ASSO_REPORT.reminders,
  ACTIVITIES_TYPES_ASSO_REPORT.salesOpps,
];

function sortByDate(a, b, type) {
  if (type === ACTIVITIES_TYPES_ASSO_REPORT.reminders) {
    return isBefore(new Date(a?.due_date), new Date(b?.due_date)) ? 1 : -1;
  }
  return isBefore(
    new Date(a?.completed || a?.created_date || a?.close_date),
    new Date(b?.completed || b?.created_date || b?.close_date),
  )
    ? 1
    : -1;
}

/**
 * Custom hook that fetches all report data when user lands on /Reports route
 * Replaces the ReportContext data fetching logic
 */
export const useReportQueries = () => {
  const authenticate = useSelector(state => state.auth.authenticated);
  const licenseKey = useSelector(state => state.auth.licenseKey);

  // 1. Chat Message Report
  const { data: chatMessages, isLoading: isLoadingChatMessage } = useQuery(
    ['chat-message-report', authenticate],
    async () => {
      const reqObj = {
        params: ['report', 'chatMessage'],
        loadingStatusOff: true,
        query: {
          licenseKey,
        },
      };
      return await getRequest(reqObj);
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    },
  );

  // 2. Reminder Report
  const { data: reminders, isLoading: isLoadingReminder } = useQuery(
    ['reminder-report', authenticate],
    async () => {
      const reqObj = {
        params: ['report', 'reminder'],
        loadingStatusOff: true,
        query: {
          licenseKey,
        },
      };
      const res = await getRequest(reqObj);
      const { reportData } = res;
      const formattedData = reportData.map(item => ({
        ...item,
        activity_associate_name: item.associate_name,
        activity_store_name: item.store_name,
        activity_date: item.complete_date,
        activity_associate_id: item.associate_id,
        activity_store_id: item.store_id,
        sales_transaction_amount: item.transaction_amount,
        sales_transaction_store_id: item.transaction_store_id,
        sales_transaction_date: item.sales_transaction_date_time,
      }));
      return {
        reportData: formattedData,
        stats: res.stats,
      };
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    },
  );

  // 3. Client Activity Report
  const { data: clientActivity, isLoading: isLoadingClientActivity } = useQuery(
    ['client-activity-report', authenticate],
    async () => {
      const reqObj = {
        params: ['report', 'clientActivity'],
        loadingStatusOff: true,
        query: {
          licenseKey,
        },
      };
      const res = await getRequest(reqObj);
      const { reportData } = res;
      const data = reportData.filter(
        item =>
          item?.sales_transaction_id &&
          item?.sales_transaction_amount &&
          parseFloat(item?.sales_transaction_amount) >= 0,
      );
      return {
        reportData: data,
      };
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    },
  );

  // 4. Appointment Report
  const { data: appointments, isLoading: isLoadingAppointments } = useQuery(
    ['appointment-report', authenticate],
    async () => {
      const reqObj = {
        params: ['report', 'appointment'],
        loadingStatusOff: true,
        query: {
          licenseKey,
        },
      };
      const res = await getRequest(reqObj);
      const { reportData, stats } = res;
      return {
        reportData,
        stats,
      };
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    },
  );

  // 5. New Clients Report
  const { data: newClients, isLoading: isLoadingNewClients } = useQuery(
    ['new-client-report', authenticate],
    async () => {
      const reqObj = {
        params: ['report', 'newClient'],
        loadingStatusOff: true,
        query: {
          licenseKey,
        },
      };
      const res = await getRequest(reqObj);
      const { reportData } = res;
      const data = reportData.filter(
        item =>
          item?.sales_transaction_id &&
          item?.sales_transaction_amount &&
          parseFloat(item?.sales_transaction_amount) >= 0,
      );
      return data.filter(item => item.activity_source);
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    },
  );

  // 6. New Clients Meta Report
  const { data: newClientsMeta, isLoading: isLoadingNewClientsMeta } = useQuery(
    ['new-client-report-meta', authenticate],
    async () => {
      const reqObj = {
        params: ['report', 'newClientMeta'],
        loadingStatusOff: true,
      };
      const res = await getRequest(reqObj);
      const { reportData } = res;
      return reportData;
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    },
  );

  // 7. Sales Opp Report (with 60 second timeout)
  const {
    data: salesOpp,
    isLoading: isLoadingSalesOpp,
    refetch: refetchSalesOpp,
  } = useQuery(
    ['sale-opp-report', authenticate],
    async () => {
      const response = await getSalesOpp();

      let overdueDate = new Date();
      response.data.reportData.forEach(item => {
        if (item.salesOppCloseDate && isBefore(new Date(item.salesOppCloseDate), overdueDate)) {
          overdueDate = new Date(item.salesOppCloseDate);
        }
      });

      return {
        opportunityTypes: response.data.opportunityTypes,
        reportData: response.data.reportData,
        overdueDate,
      };
    },
    {
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      enabled: authenticate,
    },
  );

  // 8. Associate Performance Reports (6 types)
  const allAssociatePerformanceData = useQueries(
    allAssociatePerformanceTypes.map(type => ({
      queryKey: ['associate-report', authenticate, type],
      queryFn: async () => {
        const reqObj = {
          params: ['report', 'associatePerformance', type],
          loadingStatusOff: true,
        };
        const response = await getRequest(reqObj);
        return response[type].sort((a, b) => sortByDate(a, b, type));
      },
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: authenticate,
    })),
  );

  const associatesPerformance = useMemo(() => {
    return allAssociatePerformanceData.reduce((acc, assPerf, index) => {
      const newData = assPerf?.data || [];
      const type = allAssociatePerformanceTypes[index];
      acc[type] = [...newData];
      return acc;
    }, {});
  }, [allAssociatePerformanceData]);

  const isLoadingAssociatesPerformance = useMemo(() => {
    return allAssociatePerformanceData.filter(({ isLoading }) => isLoading).length > 0;
  }, [allAssociatePerformanceData]);

  return {
    chatMessages,
    isLoadingChatMessage,
    reminders,
    isLoadingReminder,
    newClients,
    isLoadingNewClients,
    salesOpp,
    isLoadingSalesOpp,
    refetchSalesOpp,
    newClientsMeta,
    isLoadingNewClientsMeta,
    associatesPerformance,
    isLoadingAssociatesPerformance,
    clientActivity,
    isLoadingClientActivity,
    appointments,
    isLoadingAppointments,
  };
};
